/* eslint-disable no-console */

import S3getSignedUrl from 'Api/Request/s3GetSignedUrl';
import S3putSignedUrl from 'Api/Request/s3PutSignedUrl';
import S3makeSignedUrl from 'Api/Request/s3MakeSignedUrl';
import S3ListedObjects from 'Api/Request/S3ListedObjects';
import S3DeleteObject from 'Api/Request/S3DeleteObject';

const commands = Object.freeze({
  get: 'getObject',
  put: 'putObject',
  delete: 'deleteObject',
});

/**
 * Obtain a signed URL to upload a file to S3
 * @param {string} key The key of the S3 bucket
 * @param {File} contentType The content type of the file
 */
const putSignedUrl = async key => {
  try {
    const response = await S3putSignedUrl({ key });
    return response;
  } catch (caught) {
    console.error(caught);
    throw new Error('Failed to get signed URL');
  }
};

/**
 * Obtain a signed URL to download a file from S3
 * @param {string} key The key of the S3 bucket
 */
const getSignedUrl = async key => {
  try {
    const response = await S3getSignedUrl({ key });
    return response;
  } catch (caught) {
    console.error(caught);
    throw new Error('Failed to get signed URL');
  }
};

/**
 * Obtain a signed URL to do a specific command in S3
 * @param {string} key The key of the S3 bucket
 * @param {commands} command The command to execute
 */
const makeSignedUrl = async (key, command) => {
  try {
    const response = await S3makeSignedUrl({ key, command });
    return response;
  } catch (caught) {
    console.error(caught);
    throw new Error('Failed to get signed URL');
  }
};

/**
 * Upload a file to S3
 * @param {file} file The file in binary
 * @param {string} contentType The content type of the file
 */
const uploadFileToBucket = async (url, file, contentType) => {
  try {
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
        'Cache-Control': 'no-cache',
        Accept: '*/*',
      },
    });

    if (!response.ok) throw new Error('Failed to upload file');

    return true;
  } catch (caught) {
    console.error(caught);
    throw new Error('Failed to upload file');
  }
};

/**
 * Request a signed URL to back and upload the file after
 * @param {string} key The key of the S3 bucket
 * @param {Buffer} file The file in binary
 * @param {string} contentType The content type of the file
 */
const uploadFileToBucketWithKeyProvided = async (key, file, contentType) => {
  try {
    const { signedUrl } = await putSignedUrl(key);

    const success = await uploadFileToBucket(signedUrl, file, contentType);

    const signedUrlResponse = await getSignedUrl(key);

    if (!success) throw new Error('Failed to upload file');

    return signedUrlResponse;
  } catch (caught) {
    console.error(caught);
    throw new Error('Failed to upload file');
  }
};

/**
 * @typedef {Object} FileObject
 * @property {string} key
 * @property {string} prefix
 * @property {number} count
 * @property {Array<{key: string, size: number, fileName: string}>} files
 */

/**
 * Método para traer los objetos listados en un path especifico de un bucket de amazon.
 *
 * @param {key} key The key of the bucket object
 * @returns {Promise<FileObject>} A object contaning relevant information about the objects in the bucket
 */
const getListObjects = async key => {
  try {
    const data = await S3ListedObjects(key);
    return data;
  } catch (caught) {
    console.error(caught);
    throw new Error('Failed to list objects');
  }
};

/**
 * Delete an object from the S3 bucket
 * @param {string} key The key of the bucket path
 */
const deleteObject = async key => {
  try {
    const success = await S3DeleteObject(key);
    return success;
  } catch (caught) {
    console.error(caught);
    throw new Error('Failed to delete object');
  }
};

export const s3 = {
  putSignedUrl,
  getSignedUrl,
  makeSignedUrl,
  uploadFileToBucket,
  uploadFileToBucketWithKeyProvided,
  getListObjects,
  deleteObject,
};
